import type { Store } from '@reduxjs/toolkit';
import { environmentCookieDomain } from '../platform/auth/providers/stytchCookieOptions';
import {
  clearSession,
  endImpersonation,
  expireSession,
  setAppUserId,
  setError,
  setSession,
} from '../store/auth/authSlice';
import { fetchWithTelemetry } from './fetchWithTelemetry';
import { clearSessionId, rotateSessionId } from './telemetry';

// Avoid runtime circular dependency by injecting the store after creation
let injectedStore: Store | null = null;
export function setAppStore(store: Store) {
  injectedStore = store;
}

export interface DriveConnectPrompt {
  authUrl: string;
  state: string;
  expiresAt?: string;
}

export class DriveConnectRequiredError extends Error {
  prompt: DriveConnectPrompt;

  constructor(prompt: DriveConnectPrompt) {
    super('Drive connection required');
    this.prompt = prompt;
  }
}

interface TokenExchangeResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  expires_at: string;
  person?: {
    id: string;
    email: string;
    organization_id: string;
    roles: string[];
    permissions: string[];
    permissions_hash?: string;
    given_name?: string;
    family_name?: string;
    picture_url?: string;
  };
}

interface RuntimeImpersonationState {
  active: boolean;
  returnPath?: string;
  token: string;
  expiresAt: string;
  originalToken: string;
  originalUser: unknown;
}

interface RuntimeAuthState {
  impersonation: RuntimeImpersonationState | null;
}

interface RuntimeStoreState {
  auth?: RuntimeAuthState;
}

export type ActiveTokenResolution =
  | { source: 'impersonation_redux'; token: string; expiresAt: string }
  | { source: 'impersonation_storage'; token: string; expiresAt: string }
  | { source: 'platform'; token: string }
  | { source: 'none'; token: null };

function saveUserToSessionStorage(user: TokenExchangeResponse['person'] | undefined) {
  try {
    if (user) {
      const sanitizedUser = sanitizeStoredUser(user);
      const serialized = JSON.stringify(sanitizedUser);
      sessionStorage.setItem('platform_user', serialized);
      localStorage.setItem('platform_user', serialized);
    } else {
      sessionStorage.removeItem('platform_user');
      localStorage.removeItem('platform_user');
    }
  } catch {}
}

function loadUserFromSessionStorage(): TokenExchangeResponse['person'] | null {
  try {
    const raw = sessionStorage.getItem('platform_user');
    if (raw) {
      const sanitized = sanitizeStoredUser(JSON.parse(raw));
      const serialized = JSON.stringify(sanitized);
      if (raw !== serialized) {
        sessionStorage.setItem('platform_user', serialized);
        localStorage.setItem('platform_user', serialized);
      }
      return sanitized;
    }
    const persisted = localStorage.getItem('platform_user');
    if (persisted) {
      const sanitized = sanitizeStoredUser(JSON.parse(persisted));
      const serialized = JSON.stringify(sanitized);
      sessionStorage.setItem('platform_user', serialized);
      localStorage.setItem('platform_user', serialized);
      return sanitized;
    }
  } catch {}
  return null;
}

function sanitizeStoredUser(
  user: TokenExchangeResponse['person'] | unknown
): TokenExchangeResponse['person'] {
  const candidate =
    user && typeof user === 'object' ? { ...(user as Record<string, unknown>) } : {};
  delete candidate.access_mode;
  delete candidate.accessMode;

  return candidate as TokenExchangeResponse['person'];
}

/**
 * Generate a unique session ID for this browser tab.
 * Used to track which tab initiated an impersonation session.
 */
function generateImpersonationSessionId(): string {
  return `imp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

const analyticsImpersonationSuppressionCookiePrefix = 'moments_analytics_suppressed_';

export const analyticsSuppressionCookieDomain = (
  hostname = typeof window === 'undefined' ? '' : window.location.hostname
): string | null => environmentCookieDomain(hostname);

const analyticsImpersonationSuppressionCookieName = (sessionId: string): string =>
  `${analyticsImpersonationSuppressionCookiePrefix}${encodeURIComponent(sessionId)}`;

const setAnalyticsImpersonationSuppression = (sessionId: string, expiresAt: string): void => {
  if (typeof document === 'undefined') return;
  const expiresAtMs = Date.parse(expiresAt);
  const maxAgeSeconds = Math.floor((expiresAtMs - Date.now()) / 1000);
  if (!Number.isFinite(maxAgeSeconds) || maxAgeSeconds <= 0) {
    clearAnalyticsImpersonationSuppression(sessionId);
    return;
  }
  const cookieName = analyticsImpersonationSuppressionCookieName(sessionId);
  const attributes = [`${cookieName}=1`, `Max-Age=${maxAgeSeconds}`, 'Path=/', 'SameSite=Lax'];
  if (window.location.protocol === 'https:') attributes.push('Secure');
  const domain = analyticsSuppressionCookieDomain();
  if (domain) attributes.push(`Domain=${domain}`);
  document.cookie = attributes.join('; ');
};

const clearAnalyticsImpersonationSuppression = (sessionId: string): void => {
  if (typeof document === 'undefined') return;
  const cookieName = analyticsImpersonationSuppressionCookieName(sessionId);
  const baseAttributes = ['Max-Age=0', 'Path=/', 'SameSite=Lax'];
  if (window.location.protocol === 'https:') baseAttributes.push('Secure');
  document.cookie = `${cookieName}=; ${baseAttributes.join('; ')}`;
  const domain = analyticsSuppressionCookieDomain();
  if (domain) {
    document.cookie = `${cookieName}=; ${[...baseAttributes, `Domain=${domain}`].join('; ')}`;
  }
};

/**
 * Log storage errors in development mode.
 * In production, this could be extended to send to a monitoring service.
 */
function logStorageError(operation: string, error: unknown): void {
  if (import.meta.env.DEV) {
    console.warn(`[TokenService] Storage ${operation} failed:`, error);
  }
  // TODO: In production, consider sending to a monitoring service like Sentry
  // monitoringService.captureException(error, { context: 'tokenService', operation });
}

class TokenService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiresAt: Date | null = null;
  private refreshInFlight: Promise<string | null> | null = null;
  private pendingDrivePrompt: DriveConnectPrompt | null = null;
  private pendingExchangeError: string | null = null;
  // Use relative URLs - Vite proxy handles /api/* in dev, same-origin in production
  private identityServiceUrl = '';
  // Unique session ID for this browser tab - used to track impersonation ownership
  private impersonationSessionId: string;

  constructor() {
    // Generate or restore session ID for this tab (stored in sessionStorage which is tab-specific)
    // Note: Session ID is still saved with impersonation state for potential future use,
    // but we no longer use it for tab isolation during load.
    let sessionId = sessionStorage.getItem('impersonation_session_id');
    if (!sessionId) {
      sessionId = generateImpersonationSessionId();
      sessionStorage.setItem('impersonation_session_id', sessionId);
    }
    this.impersonationSessionId = sessionId;
    this.loadPersistedTokensIntoMemory();
  }

  private loadPersistedTokensIntoMemory(): void {
    try {
      const localAccessToken = localStorage.getItem('platform_access_token');
      const localRefreshToken = localStorage.getItem('platform_refresh_token');
      const localExpiresAt = localStorage.getItem('platform_expires_at');

      const sessionAccessToken = sessionStorage.getItem('platform_access_token');
      const sessionRefreshToken = sessionStorage.getItem('platform_refresh_token');
      const sessionExpiresAt = sessionStorage.getItem('platform_expires_at');

      const at = localAccessToken ?? sessionAccessToken;
      const rt = localRefreshToken ?? sessionRefreshToken;
      const exp = localExpiresAt ?? sessionExpiresAt;

      if (!localAccessToken && sessionAccessToken) {
        localStorage.setItem('platform_access_token', sessionAccessToken);
      }
      if (!localRefreshToken && sessionRefreshToken) {
        localStorage.setItem('platform_refresh_token', sessionRefreshToken);
      }
      if (!localExpiresAt && sessionExpiresAt) {
        localStorage.setItem('platform_expires_at', sessionExpiresAt);
      }

      this.accessToken = at;
      this.refreshToken = rt;
      if (!exp) {
        this.tokenExpiresAt = null;
        return;
      }

      const parsed = new Date(exp);
      this.tokenExpiresAt = Number.isNaN(parsed.getTime()) ? null : parsed;
    } catch {}
  }

  consumeDrivePrompt(): DriveConnectPrompt | null {
    const prompt = this.pendingDrivePrompt;
    this.pendingDrivePrompt = null;
    return prompt;
  }

  peekDrivePrompt(): DriveConnectPrompt | null {
    return this.pendingDrivePrompt;
  }

  consumeExchangeError(): string | null {
    const error = this.pendingExchangeError;
    this.pendingExchangeError = null;
    return error;
  }

  private getRuntimeImpersonationState(): RuntimeImpersonationState | null {
    if (!injectedStore) {
      return null;
    }

    const state = injectedStore.getState() as RuntimeStoreState;
    const impersonation = state.auth?.impersonation;

    if (!impersonation?.active) {
      return null;
    }

    return impersonation;
  }

  private isValidFutureTime(expiresAt: string | undefined): boolean {
    if (!expiresAt) {
      return false;
    }
    const expiry = new Date(expiresAt);
    if (Number.isNaN(expiry.getTime())) {
      return false;
    }
    return expiry > new Date();
  }

  private clearImpersonationStorage(): void {
    try {
      localStorage.removeItem('impersonation_state');
    } catch (error) {
      logStorageError('clearImpersonationState', error);
    }
    // Each tab owns its marker. Clearing this tab must not resume analytics in
    // another tab that is still impersonating.
    clearAnalyticsImpersonationSuppression(this.impersonationSessionId);
  }

  private clearRuntimeImpersonationState(options?: { restoreAdminSession?: boolean }): void {
    const restoreAdminSession = options?.restoreAdminSession !== false;
    const runtimeImpersonation = this.getRuntimeImpersonationState();

    this.clearImpersonationStorage();

    if (!runtimeImpersonation) {
      return;
    }

    if (restoreAdminSession && runtimeImpersonation.originalToken) {
      const originalUser =
        runtimeImpersonation.originalUser === null
          ? undefined
          : (runtimeImpersonation.originalUser as TokenExchangeResponse['person'] | undefined);
      injectedStore?.dispatch(
        setSession({
          token: runtimeImpersonation.originalToken,
          user: originalUser,
        })
      );
    }

    injectedStore?.dispatch(endImpersonation());
  }

  async resolveActiveToken(): Promise<ActiveTokenResolution> {
    const runtimeImpersonation = this.getRuntimeImpersonationState();
    if (runtimeImpersonation) {
      if (runtimeImpersonation.token && this.isValidFutureTime(runtimeImpersonation.expiresAt)) {
        return {
          source: 'impersonation_redux',
          token: runtimeImpersonation.token,
          expiresAt: runtimeImpersonation.expiresAt,
        };
      }
      console.warn('[TokenService] Redux impersonation expired, clearing and falling back');
      this.clearRuntimeImpersonationState();
    }

    const persistedImpersonation = this.loadImpersonationState();
    if (
      persistedImpersonation?.active &&
      persistedImpersonation.token &&
      persistedImpersonation.expiresAt
    ) {
      return {
        source: 'impersonation_storage',
        token: persistedImpersonation.token,
        expiresAt: persistedImpersonation.expiresAt,
      };
    }

    const platformToken = await this.getAccessToken();
    if (platformToken) {
      return { source: 'platform', token: platformToken };
    }

    return { source: 'none', token: null };
  }

  /**
   * Exchange a Stytch session for a first-party JWT
   */
  async exchangeStytchSession(stytchSessionToken: string): Promise<string | null> {
    return this.exchangeStytchSessionInternal(stytchSessionToken, false);
  }

  /**
   * Exchange using HttpOnly Stytch cookies when frontend JS cannot read cookie values.
   */
  async exchangeStytchSessionFromCookie(): Promise<string | null> {
    return this.exchangeStytchSessionInternal(undefined, true);
  }

  private async exchangeStytchSessionInternal(
    stytchSessionToken?: string,
    silentFailure = false
  ): Promise<string | null> {
    this.pendingExchangeError = null;
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      const exchangeBody: Record<string, unknown> = {
        client: 'web-main',
        requested_audience: ['identity'],
        requested_scopes: ['read', 'write'],
      };
      if (stytchSessionToken) {
        exchangeBody.stytch_session_token = stytchSessionToken;
      }

      const response = await fetchWithTelemetry(
        `${this.identityServiceUrl}/api/v1/auth/token/exchange`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(exchangeBody),
        }
      );

      if (response.status === 428) {
        let payload: any = {};
        try {
          payload = await response.json();
        } catch {}
        const prompt: DriveConnectPrompt = {
          authUrl: typeof payload?.auth_url === 'string' ? payload.auth_url : '',
          state: typeof payload?.state === 'string' ? payload.state : '',
          expiresAt: typeof payload?.expires_at === 'string' ? payload.expires_at : undefined,
        };
        this.pendingDrivePrompt = prompt;
        return null;
      }

      if (!response.ok) {
        let errorDescription = '';
        try {
          const payload = await response.json();
          errorDescription =
            typeof payload?.error_description === 'string' ? payload.error_description : '';
        } catch {}
        this.pendingExchangeError = errorDescription || null;
        if (!silentFailure) {
          injectedStore?.dispatch(setError('Authentication failed'));
        }
        return null;
      }

      const data: TokenExchangeResponse = await response.json();

      // Store tokens (memory + localStorage for persistence across tabs/external links)
      this.accessToken = data.access_token;
      this.refreshToken = data.refresh_token;
      this.tokenExpiresAt = new Date(data.expires_at);
      this.pendingDrivePrompt = null;

      try {
        rotateSessionId();
      } catch (error) {
        logStorageError('rotateSessionId', error);
      }

      try {
        localStorage.setItem('platform_access_token', data.access_token);
        localStorage.setItem('platform_refresh_token', data.refresh_token);
        localStorage.setItem('platform_expires_at', data.expires_at);
        // Keep sessionStorage in sync for backward compatibility
        sessionStorage.setItem('platform_access_token', data.access_token);
        sessionStorage.setItem('platform_refresh_token', data.refresh_token);
        sessionStorage.setItem('platform_expires_at', data.expires_at);
      } catch (error) {
        logStorageError('persistExchangedTokens', error);
      }

      // Update Redux store with the JWT and user info
      saveUserToSessionStorage(data.person);
      injectedStore?.dispatch(
        setSession({
          token: data.access_token,
          user: data.person,
        })
      );
      // Optionally pre-hydrate appUserId if identity user_id matches app user id in dev
      try {
        const raw = sessionStorage.getItem('app_user_id');
        if (raw) {
          const parsed = Number(raw);
          if (Number.isFinite(parsed)) {
            injectedStore?.dispatch(setAppUserId(parsed));
          }
        }
      } catch {}

      return data.access_token;
    } catch (_error) {
      if (!silentFailure) {
        injectedStore?.dispatch(setError('Authentication failed'));
      }
      return null;
    }
  }

  /**
   * Dev-only login that bypasses Stytch entirely.
   * Calls the backend /api/v1/auth/dev-login endpoint.
   */
  async devLogin(email = 'dev@localhost'): Promise<string | null> {
    try {
      const response = await fetchWithTelemetry(
        `${this.identityServiceUrl}/api/v1/auth/dev-login`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        injectedStore?.dispatch(setError('Dev login failed'));
        return null;
      }

      const data: TokenExchangeResponse = await response.json();

      this.accessToken = data.access_token;
      this.refreshToken = data.refresh_token;
      this.tokenExpiresAt = new Date(data.expires_at);
      this.pendingDrivePrompt = null;

      try {
        rotateSessionId();
      } catch (error) {
        logStorageError('rotateSessionId', error);
      }

      try {
        localStorage.setItem('platform_access_token', data.access_token);
        localStorage.setItem('platform_refresh_token', data.refresh_token);
        localStorage.setItem('platform_expires_at', data.expires_at);
        sessionStorage.setItem('platform_access_token', data.access_token);
        sessionStorage.setItem('platform_refresh_token', data.refresh_token);
        sessionStorage.setItem('platform_expires_at', data.expires_at);
      } catch (error) {
        logStorageError('persistDevLoginTokens', error);
      }

      saveUserToSessionStorage(data.person);
      injectedStore?.dispatch(
        setSession({
          token: data.access_token,
          user: data.person,
        })
      );

      return data.access_token;
    } catch (_err) {
      injectedStore?.dispatch(setError('Dev login failed'));
      return null;
    }
  }

  /**
   * Get the currently active token - returns impersonation token if active, otherwise normal token.
   * Used by API slices to get the appropriate token for requests.
   */
  async getActiveToken(): Promise<string | null> {
    const resolved = await this.resolveActiveToken();
    return resolved.token;
  }

  /**
   * Get impersonation token if active and not expired.
   * This accessor is intentionally side-effect free; cleanup/teardown belongs
   * to resolveActiveToken(), which runs on authenticated request paths.
   * Note: We no longer check session ID for tab isolation. If impersonation is active
   * in localStorage, any tab can use it. This simplifies refresh handling and the
   * security model still holds since impersonation tokens are short-lived and audit-logged.
   */
  getImpersonationToken(): string | null {
    const runtimeImpersonation = this.getRuntimeImpersonationState();
    if (runtimeImpersonation) {
      if (runtimeImpersonation.token && this.isValidFutureTime(runtimeImpersonation.expiresAt)) {
        return runtimeImpersonation.token;
      }
      return null;
    }

    const persistedImpersonation = this.loadImpersonationState();
    if (persistedImpersonation?.active && persistedImpersonation.token) {
      return persistedImpersonation.token;
    }

    return null;
  }

  /**
   * Check if currently in impersonation mode
   */
  isImpersonating(): boolean {
    return this.getImpersonationToken() !== null;
  }

  /**
   * Save impersonation state to localStorage.
   * Includes this tab's session ID to prevent other tabs from using this impersonation.
   */
  saveImpersonationState(state: {
    targetId: string;
    targetName: string;
    targetEmail: string;
    targetOrgId: string;
    targetRoles: string[];
    targetPermissions: string[];
    returnPath?: string;
    token: string;
    expiresAt: string;
    originalToken: string;
    originalUser: unknown;
  }): void {
    try {
      const stateWithSession = {
        active: true,
        sessionId: this.impersonationSessionId, // Track which tab initiated this impersonation
        ...state,
      };
      localStorage.setItem('impersonation_state', JSON.stringify(stateWithSession));
    } catch (error) {
      logStorageError('saveImpersonationState', error);
    }
    // Analytics suppression must survive a browser-storage failure: Redux still
    // enters impersonation mode, and the server must receive the same signal.
    setAnalyticsImpersonationSuppression(this.impersonationSessionId, state.expiresAt);
  }

  /**
   * Clear impersonation state from localStorage
   */
  clearImpersonationState(): void {
    this.clearImpersonationStorage();
  }

  clearRuntimeImpersonation(options?: { restoreAdminSession?: boolean }): void {
    this.clearRuntimeImpersonationState(options);
  }

  /**
   * Load impersonation state from localStorage.
   * Only returns state if it was initiated by this tab (session ID matches).
   */
  loadImpersonationState(): {
    active: boolean;
    sessionId?: string;
    targetId: string;
    targetName: string;
    targetEmail: string;
    targetOrgId: string;
    targetRoles?: string[];
    targetPermissions?: string[];
    returnPath?: string;
    token: string;
    expiresAt: string;
    originalToken: string;
    originalUser: unknown;
  } | null {
    try {
      const state = localStorage.getItem('impersonation_state');
      if (state) {
        const parsed = JSON.parse(state);
        if (parsed?.active && parsed?.expiresAt) {
          // Note: We no longer check session ID for tab isolation.
          // If impersonation is active in localStorage, any tab can use it.
          // This simplifies refresh handling and the security model still holds
          // since impersonation tokens are short-lived and audit-logged.
          const expiresAt = new Date(parsed.expiresAt);
          if (expiresAt > new Date()) {
            // A tab that joins an existing impersonation session gets its own
            // marker, so ending one tab cannot resume analytics in another.
            setAnalyticsImpersonationSuppression(this.impersonationSessionId, parsed.expiresAt);
            return parsed;
          }
          // Expired - clear
          this.clearImpersonationStorage();
        }
      }
    } catch (error) {
      logStorageError('loadImpersonationState', error);
    }
    return null;
  }

  /**
   * Get the current access token, refreshing if needed
   */
  async getAccessToken(): Promise<string | null> {
    // debug logging removed

    // If we have a valid token with >1 minute remaining, return it
    const tokenStillValid = () => {
      if (!this.accessToken || !this.tokenExpiresAt) return null;
      const currentTime = new Date();
      return this.tokenExpiresAt > currentTime ? this.accessToken : null;
    };

    const now = new Date();
    const oneMinuteFromNow = new Date(now.getTime() + 60 * 1000);

    if (this.accessToken && this.tokenExpiresAt) {
      if (this.tokenExpiresAt <= now) {
        // Token expired; fall through to refresh/rehydration paths.
      } else if (this.tokenExpiresAt > oneMinuteFromNow) {
        // Plenty of runway left, just use the current token.
        return this.accessToken;
      } else {
        // Token is valid but inside the 60s window—try to refresh, but fall back to existing token if refresh fails.
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          return refreshed;
        }
        const stillOk = tokenStillValid();
        if (stillOk) {
          return stillOk;
        }
      }
    }

    // If we have a refresh token, try to refresh
    if (this.refreshToken) {
      // debug logging removed
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        return refreshed;
      }
      const stillOk = tokenStillValid();
      if (stillOk) {
        return stillOk;
      }
    }

    // Try to rehydrate from localStorage first, then sessionStorage
    // debug logging removed
    try {
      this.loadPersistedTokensIntoMemory();
      if (this.accessToken && this.tokenExpiresAt) {
        if (this.tokenExpiresAt && this.tokenExpiresAt > now) {
          if (this.tokenExpiresAt > oneMinuteFromNow) {
            return this.accessToken;
          }
          const refreshed = await this.refreshAccessToken();
          if (refreshed) {
            return refreshed;
          }
          const stillOk = tokenStillValid();
          if (stillOk) {
            return stillOk;
          }
        }
      }
      if (this.refreshToken) {
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          return refreshed;
        }
        const stillOk = tokenStillValid();
        if (stillOk) {
          return stillOk;
        }
      }
    } catch {}

    // No valid tokens
    return null;
  }

  /**
   * Refresh the access token using the refresh token
   */
  private async refreshAccessToken(): Promise<string | null> {
    if (!this.refreshToken) {
      return null;
    }

    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const refreshPromise = this.performRefreshAccessToken(this.refreshToken);
    this.refreshInFlight = refreshPromise;
    try {
      return await refreshPromise;
    } finally {
      if (this.refreshInFlight === refreshPromise) {
        this.refreshInFlight = null;
      }
    }
  }

  private async performRefreshAccessToken(requestRefreshToken: string): Promise<string | null> {
    try {
      const response = await fetchWithTelemetry(
        `${this.identityServiceUrl}/api/v1/auth/token/refresh`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            refresh_token: requestRefreshToken,
          }),
        }
      );

      if (!response.ok) {
        // Log the specific error for debugging
        console.warn(`[TokenService] Token refresh failed with status ${response.status}`);
        let errorCode = '';
        let errorDetail = '';
        try {
          const errorData = await response.json();
          errorCode = errorData.error || '';
          errorDetail = errorCode || errorData.message || '';
        } catch {}

        if (response.status === 409 && errorCode === 'refresh_token_conflict') {
          return this.recoverFromRefreshConflict(requestRefreshToken);
        }

        // Only clear tokens and show session expired if it's a 401/403 (truly expired)
        // For other errors (network, 500, etc.), keep the current token and let retry happen
        if (response.status === 401 || response.status === 403) {
          console.warn('[TokenService] Refresh token is invalid or expired, clearing session');
          this.clearTokens({
            expired: true,
            message: 'Your session has expired. Please sign in again.',
          });
        } else {
          console.warn(`[TokenService] Token refresh failed but will retry: ${errorDetail}`);
        }
        return null;
      }

      const data: TokenExchangeResponse = await response.json();

      // Update tokens (memory + localStorage for persistence across tabs/external links)
      this.accessToken = data.access_token;
      this.refreshToken = data.refresh_token;
      this.tokenExpiresAt = new Date(data.expires_at);
      try {
        localStorage.setItem('platform_access_token', data.access_token);
        localStorage.setItem('platform_refresh_token', data.refresh_token);
        localStorage.setItem('platform_expires_at', data.expires_at);
        // Keep sessionStorage in sync for backward compatibility
        sessionStorage.setItem('platform_access_token', data.access_token);
        sessionStorage.setItem('platform_refresh_token', data.refresh_token);
        sessionStorage.setItem('platform_expires_at', data.expires_at);
      } catch {}

      // ENHANCEMENT: If refresh response includes person data, use it (it has fresh roles)
      if (data.person) {
        saveUserToSessionStorage(data.person);
        injectedStore?.dispatch(
          setSession({
            token: data.access_token,
            user: data.person, // Use fresh data from server
          })
        );
      } else {
        // Fallback: preserve existing user if refresh doesn't include person
        const existingUser =
          (injectedStore?.getState() as any)?.auth?.user ?? loadUserFromSessionStorage();
        saveUserToSessionStorage(existingUser ?? undefined);
        injectedStore?.dispatch(
          setSession({
            token: data.access_token,
            user: existingUser ?? undefined,
          })
        );
      }
      try {
        const raw = sessionStorage.getItem('app_user_id');
        if (raw) {
          const parsed = Number(raw);
          if (Number.isFinite(parsed)) {
            injectedStore?.dispatch(setAppUserId(parsed));
          }
        }
      } catch {}

      return data.access_token;
    } catch (err) {
      // Network error or other exception - don't clear tokens, just log and return null
      console.warn('[TokenService] Token refresh threw exception (network error?)', err);
      return null;
    }
  }

  private async recoverFromRefreshConflict(attemptedRefreshToken: string): Promise<string | null> {
    this.loadPersistedTokensIntoMemory();

    const now = new Date();
    const oneMinuteFromNow = new Date(now.getTime() + 60 * 1000);
    if (this.accessToken && this.tokenExpiresAt && this.tokenExpiresAt > oneMinuteFromNow) {
      return this.accessToken;
    }

    if (this.refreshToken && this.refreshToken !== attemptedRefreshToken) {
      return this.performRefreshAccessToken(this.refreshToken);
    }

    if (this.accessToken && this.tokenExpiresAt && this.tokenExpiresAt > now) {
      return this.accessToken;
    }

    return null;
  }

  /**
   * Invalidate the current access token, forcing next getAccessToken() call
   * to use the refresh token to obtain a new one.
   *
   * Use this when the server returns 401/419 indicating the access token is
   * invalid server-side, even though the client thinks it's still valid.
   */
  invalidateAccessToken(): void {
    this.accessToken = null;
    this.tokenExpiresAt = null;
    try {
      localStorage.removeItem('platform_access_token');
      localStorage.removeItem('platform_expires_at');
      sessionStorage.removeItem('platform_access_token');
      sessionStorage.removeItem('platform_expires_at');
    } catch {
      // ignore storage errors
    }
  }

  /**
   * Clear all stored tokens
   */
  clearTokens(options?: { expired?: boolean; message?: string }): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiresAt = null;
    this.pendingDrivePrompt = null;
    this.clearImpersonationStorage();
    try {
      // Clear from both localStorage and sessionStorage
      localStorage.removeItem('platform_access_token');
      localStorage.removeItem('platform_refresh_token');
      localStorage.removeItem('platform_expires_at');
      clearSessionId();
      sessionStorage.removeItem('platform_access_token');
      sessionStorage.removeItem('platform_refresh_token');
      sessionStorage.removeItem('platform_expires_at');
      sessionStorage.removeItem('platform_user');
      sessionStorage.removeItem('app_user_id');
      localStorage.removeItem('platform_user');
    } catch {}
    if (options?.expired) {
      injectedStore?.dispatch(expireSession({ message: options.message }));
    } else {
      injectedStore?.dispatch(clearSession());
    }
  }

  /**
   * Revoke refresh token (best-effort) and clear client-side tokens
   */
  async signOut(): Promise<void> {
    try {
      const rt = this.refreshToken || sessionStorage.getItem('platform_refresh_token');
      if (rt) {
        const headers = await this.buildAuthHeaders();
        await fetchWithTelemetry(`${this.identityServiceUrl}/api/v1/auth/token/revoke`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ refresh_token: rt }),
        }).catch(() => undefined);
      }
    } catch {
      // ignore revoke errors
    } finally {
      this.clearTokens();
    }
  }

  /**
   * Build headers with the JWT token for API requests
   * IMPORTANT: Uses getActiveToken() to respect impersonation mode
   */
  async buildAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.getActiveToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Hydrate Redux auth state from sessionStorage if possible.
   * Returns true if a valid token is available and state was set.
   */
  async hydrateFromStorage(): Promise<boolean> {
    try {
      // Ensure in-memory tokens are loaded
      const token = await this.getAccessToken();
      if (!token) return false;
      const user = loadUserFromSessionStorage() ?? undefined;
      injectedStore?.dispatch(setSession({ token, user, userResolved: false }));
      return true;
    } catch (_e) {
      return false;
    }
  }
}

/**
 * Extract Person UUID from JWT token claims
 * Returns the person_id from identity.person_id
 * This matches what the backend uses for Mixpanel distinct_id
 */
export function getPersonIdFromToken(token: string | null): string | null {
  if (!token) return null;

  try {
    // JWT tokens are base64url encoded, split by '.'
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    // Convert base64url to base64: replace - with + and _ with /
    // Add padding if needed (base64 strings should be multiples of 4 characters)
    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4;
    if (padding) {
      base64 += '='.repeat(4 - padding);
    }
    const decoded = JSON.parse(atob(base64));

    // Our JWT structure: { identity: { person_id: "uuid" } }
    return decoded.identity?.person_id || null;
  } catch (e) {
    console.error('Failed to decode token for PersonID:', e);
    return null;
  }
}

// Export singleton instance
export const tokenService = new TokenService();

// Expose in dev builds for manual testing
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).__tokenService = tokenService;
}
